import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  Info,
  Link2,
  PlugZap,
  Sparkles,
  ArrowDown,
} from "lucide-react";
import { showToast } from "../../../utils/showToast";
import { apiBodyMessage } from "../../../utils/apiErrorMessage";
import {
  getChannelIntegrations,
  connectMetaChannel,
  disconnectChannel,
  testChannelIntegration,
} from "../../../api/channelIntegrations";
import useIsMobile from "../../../hooks/useIsMobile";
import IntegrationCard from "./components/IntegrationCard";
import DisconnectModal from "./components/DisconnectModal";
import UnofficialWhatsAppManager from "./components/UnofficialWhatsAppManager";
import { cn } from "../../../utils/cn";
import { Button } from "../../../components/ui/Button";
import { Skeleton } from "../../../components/ui/Skeleton";

const META_APP_ID = process.env.REACT_APP_META_APP_ID;
const WA_CONFIG_ID = process.env.REACT_APP_META_WA_CONFIG_ID;
const MSG_CONFIG_ID = process.env.REACT_APP_META_MSG_CONFIG_ID;

const CHANNELS_CONFIG = [
  {
    key: "whatsapp",
    label: "WhatsApp Business",
    color: "#25D366",
    configId: WA_CONFIG_ID,
    descriptionKey:
      "Recibe y responde mensajes de WhatsApp directamente desde la mesa de ayuda.",
    autoNoteKey: null,
  },
  {
    key: "messenger",
    label: "Messenger",
    color: "#0084FF",
    configId: MSG_CONFIG_ID,
    descriptionKey:
      "Conecta tu página de Facebook para gestionar mensajes de Messenger.",
    autoNoteKey: null,
  },
  {
    key: "instagram",
    label: "Instagram",
    color: "#E4405F",
    configId: null,
    descriptionKey: "Gestiona mensajes directos de Instagram desde aquí.",
    autoNoteKey:
      "Se activa automáticamente al conectar Messenger si tu página tiene Instagram Business asociado.",
  },
];

let fbSdkLoaded = false;
let fbSdkLoading = false;

function loadFacebookSDK() {
  return new Promise((resolve) => {
    if (fbSdkLoaded) {
      resolve();
      return;
    }
    if (fbSdkLoading) {
      const interval = setInterval(() => {
        if (fbSdkLoaded) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
      return;
    }
    fbSdkLoading = true;
    window.fbAsyncInit = function () {
      window.FB.init({ appId: META_APP_ID, cookie: true, xfbml: false, version: "v25.0" });
      fbSdkLoaded = true;
      fbSdkLoading = false;
      resolve();
    };
    const script = document.createElement("script");
    script.src = "https://connect.facebook.net/en_US/sdk.js";
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);
  });
}

// ─── Icono de canal (SVG inline para respetar colores oficiales) ────────────
function ChannelLogo({ kind, className = "w-5 h-5" }) {
  if (kind === "whatsapp") {
    return (
      <svg viewBox="0 0 24 24" className={cn(className, "fill-current text-[#25D366]")} aria-hidden="true">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
      </svg>
    );
  }
  if (kind === "messenger") {
    return (
      <svg viewBox="0 0 24 24" className={cn(className, "fill-current text-[#0084FF]")} aria-hidden="true">
        <path d="M12 0C5.373 0 0 4.975 0 11.111c0 3.497 1.745 6.616 4.472 8.652V24l4.086-2.242c1.09.301 2.246.464 3.442.464 6.627 0 12-4.974 12-11.111C24 4.975 18.627 0 12 0zm1.193 14.963l-3.056-3.259-5.963 3.259L10.733 8.4l3.13 3.259 5.889-3.259-6.56 6.563z" />
      </svg>
    );
  }
  if (kind === "instagram") {
    return (
      <svg viewBox="0 0 24 24" className={cn(className, "fill-current text-[#E4405F]")} aria-hidden="true">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
      </svg>
    );
  }
  return null;
}

// ─── Card de un canal ────────────────────────────────────────────────────────
function ChannelCard({
  channel,
  linked,
  isConnecting,
  onConnect,
  onTest,
  testingId,
  onDisconnect,
  t,
}) {
  const isAuto = !!channel.autoNote;
  const isConnected = linked.length > 0;
  const cantConnect = !channel.configId;
  const isMobile = useIsMobile();

  return (
    <article
      className={cn(
        "flex flex-col gap-3 p-4 rounded-xl border bg-bg transition-colors",
        isConnected
          ? "border-status-success/30 bg-status-success-soft/10"
          : "border-border",
      )}
    >
      {/* Header del card */}
      <header className="flex items-start gap-3">
        <div
          className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${channel.color}14` }}
        >
          <ChannelLogo kind={channel.key} className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-fg leading-tight">{channel.label}</h3>
          <div className="mt-1 flex items-center gap-1.5 flex-wrap">
            {isConnected && (
              <span className="inline-flex items-center gap-1 px-1.5 h-5 md:h-4 rounded-full text-[12px] md:text-[10px] font-medium bg-status-success-soft text-status-success">
                <CheckCircle2 className="w-2.5 h-2.5" aria-hidden="true" />
                {t("Activo")}
              </span>
            )}
            {isAuto && !isConnected && (
              <span className="inline-flex items-center gap-1 px-1.5 h-5 md:h-4 rounded-full text-[12px] md:text-[10px] font-medium bg-bg-muted text-fg-muted">
                <Link2 className="w-2.5 h-2.5" aria-hidden="true" />
                {t("Vinculado con Messenger")}
              </span>
            )}
          </div>
        </div>
      </header>

      {/* Descripción */}
      <p className="text-xs text-fg-muted leading-relaxed">{channel.description}</p>

      {/* Cuentas vinculadas */}
      {linked.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {linked.map((integration) => (
            <IntegrationCard
              key={integration.id}
              integration={integration}
              onDisconnect={onDisconnect}
              onTest={onTest}
              testingId={testingId}
            />
          ))}
        </div>
      )}

      {/* Nota automática (Instagram) */}
      {isAuto && !isConnected && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-md bg-bg-muted/60 text-[12px] md:text-[11px] text-fg-muted leading-relaxed">
          <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-fg-subtle" aria-hidden="true" />
          <span>{channel.autoNote}</span>
        </div>
      )}

      {/* Botón conectar */}
      {!isAuto && (
        // A6/D3 · En móvil el aviso va ANTES del botón y a 12px: antes el
        // usuario tocaba primero —un CTA naranjo a ancho completo, lo más
        // llamativo de la tarjeta— y leía el motivo después, a 10px y en
        // fg-subtle.
        //
        // ⚠️ EL DOM Y LAS CLASES SON LAS DEL BASELINE: div en bloque, <p>
        // DESPUÉS del <Button>, con `mt-1.5 text-[10px] text-fg-subtle
        // text-center`. El intento anterior (`order-first md:order-last` sobre
        // un `flex flex-col`) no podía funcionar: App.js:9 carga Bootstrap
        // DESPUÉS de Tailwind y Bootstrap declara `.order-first{order:-1
        // !important}`, que ninguna variante `md:` de Tailwind revierte.
        // Medido en :3100 contra :3107 a 1000/1280/1920: `order` computaba -1 y
        // el aviso quedaba ENCIMA del botón también en escritorio.
        // El reordenado móvil vive ahora en helpdeskMobile.scss
        // (`.hd-connect-slot` / `.hd-connect-note`), bajo `max-width: 767.98px`
        // y con la especificidad necesaria.
        <div className="mt-auto pt-1 hd-connect-slot">
          <Button
            /* Sin capacidad de conectar, en móvil el botón deja de ir vestido
               de acción primaria: compite con las acciones vivas y no hace
               nada. En escritorio se conserva el aspecto anterior. */
            variant={isConnected || (cantConnect && isMobile) ? "outline" : "primary"}
            /* `hd-brand-solid` SOLO cuando el botón va sólido: sobre la
               variante outline pintaría un fondo ladrillo donde no hay
               ninguno. */
            className={
              isConnected || (cantConnect && isMobile) ? undefined : "hd-brand-solid"
            }
            size="sm"
            fullWidth
            onClick={() => onConnect(channel)}
            disabled={isConnecting || cantConnect}
            loading={isConnecting}
            iconLeft={
              !isConnecting ? (
                <PlugZap className="w-3.5 h-3.5" aria-hidden="true" />
              ) : undefined
            }
          >
            {isConnecting
              ? t("Conectando…")
              : isConnected
              ? t("Vincular otra cuenta")
              : t("Conectar")}
          </Button>
          {cantConnect && (
            <p className="hd-connect-note mt-1.5 text-[10px] text-fg-subtle text-center">
              {t("Configuración de Meta no disponible")}
            </p>
          )}
        </div>
      )}
    </article>
  );
}

// ─── Skeleton de un card de canal (shape-matched con ChannelCard) ───────────
function ChannelCardSkeleton() {
  return (
    <div className="flex flex-col gap-3 p-4 rounded-xl border border-border bg-bg">
      <div className="flex items-start gap-3">
        <Skeleton className="shrink-0 w-10 h-10 rounded-lg" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-3 w-16 rounded-full" />
        </div>
      </div>
      <Skeleton className="h-3 w-full" />
      <Skeleton className="h-3 w-3/4" />
      <Skeleton className="mt-auto h-9 w-full rounded-md" />
    </div>
  );
}

// ─── ChannelIntegrations ────────────────────────────────────────────────────
export default function ChannelIntegrations() {
  const { t } = useTranslation();
  const CHANNELS = CHANNELS_CONFIG.map((ch) => ({
    ...ch,
    description: t(ch.descriptionKey),
    autoNote: ch.autoNoteKey ? t(ch.autoNoteKey) : null,
  }));

  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [disconnectTarget, setDisconnectTarget] = useState(null);

  const fetchIntegrations = useCallback(async () => {
    setLoading(true);
    const data = await getChannelIntegrations();
    if (data?.ok) {
      setIntegrations(data.integrations || []);
    } else {
      showToast(
        data?.error?.message ?? t("No se pudieron cargar las integraciones de canal"),
        false,
      );
    }
    setLoading(false);
    // `t` is left out on purpose: a new `t` must not refetch the list (it only
    // translates the fallback of a failed load).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchIntegrations();
  }, [fetchIntegrations]);

  const handleConnect = async (channelConfig) => {
    if (!META_APP_ID || !channelConfig.configId) {
      showToast(
        t("La configuración de Meta no está completa. Contacta al administrador."),
        false,
      );
      return;
    }
    setConnecting(channelConfig.key);
    try {
      await loadFacebookSDK();

      // Listener para eventos del flujo Embedded Signup v4 (WA_EMBEDDED_SIGNUP).
      // Captura errores con código (ej. "not eligible for advertising"), abandonos
      // y confirmación de assets generados al completar el flujo.
      const handleEsMessage = (event) => {
        if (!event.origin.endsWith("facebook.com")) return;
        try {
          const data = JSON.parse(event.data);
          if (data.type !== "WA_EMBEDDED_SIGNUP") return;
          if (data.event === "CANCEL") {
            if (data.data?.error_code) {
              console.error("[WhatsApp Signup] Error en flujo:", data.data);
              showToast(
                t("Error al conectar (código {{code}}). Verifica el estado de tu cuenta en business.facebook.com", {
                  code: data.data.error_code,
                }),
                false,
              );
            } else {
              console.warn("[WhatsApp Signup] Flujo abandonado en paso:", data.data?.current_step);
            }
          } else if (data.event?.startsWith("FINISH")) {
            console.info("[WhatsApp Signup] Flujo completado:", data.data);
          }
        } catch {
          // mensajes no-JSON del popup de Facebook; se ignoran.
        }
      };
      window.addEventListener("message", handleEsMessage);

      window.FB.login(
        (response) => {
          window.removeEventListener("message", handleEsMessage);
          (async () => {
            if (response.authResponse?.code) {
              const result = await connectMetaChannel({
                code: response.authResponse.code,
                channel_hint:
                  channelConfig.key === "instagram" ? "messenger" : channelConfig.key,
                redirect_uri: window.location.origin,
              });
              if (result?.ok) {
                showToast(
                  t("{{label}} conectado correctamente", { label: channelConfig.label }),
                  true,
                );
                fetchIntegrations();
              } else {
                // `result.error` is an object ({message, status, code}); only its
                // text may reach the toast, never the object itself.
                showToast(result?.error?.message ?? t("Error al conectar el canal"), false);
              }
            } else {
              showToast(t("Vinculación cancelada"), false);
            }
            setConnecting(null);
          })();
        },
        {
          config_id: channelConfig.configId,
          response_type: "code",
          override_default_response_type: true,
          extras: { setup: {} },
        },
      );
    } catch (err) {
      showToast(t("Error al cargar el SDK de Facebook"), false);
      setConnecting(null);
    }
  };

  const handleDisconnect = async (integrationId) => {
    const result = await disconnectChannel(integrationId);
    if (result?.ok) {
      showToast(t("Integración desconectada"), true);
      fetchIntegrations();
    } else {
      // `result.error` is the full Nest body; show its message (e.g. the 404
      // when the integration was already disconnected) before the generic text.
      showToast(
        apiBodyMessage(result?.error) ?? t("Error al desconectar la integración"),
        false,
      );
    }
  };

  const handleTest = async (integrationId) => {
    setTestingId(integrationId);
    const result = await testChannelIntegration(integrationId);
    if (result?.ok && result?.valid) {
      showToast(t("Conexión verificada correctamente"), true);
    } else {
      // `result.error` is either the Nest error body (HTTP error) or a plain
      // string (200 with valid:false); apiBodyMessage handles both.
      showToast(
        apiBodyMessage(result?.error) ?? t("La conexión no es válida"),
        false,
      );
      fetchIntegrations();
    }
    setTestingId(null);
  };

  const getLinked = (channelKey) =>
    integrations.filter((i) => i.channel === channelKey && i.status === "active");

  const totalConnected = integrations.filter((i) => i.status === "active").length;
  const isFirstTime = !loading && totalConnected === 0;

  if (loading) {
    return (
      <div className="w-full min-w-0 space-y-6">
        <section>
          <Skeleton className="h-3 w-32 mb-2" />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <ChannelCardSkeleton key={i} />
            ))}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="w-full min-w-0 space-y-6">
      {/* Onboarding banner: primera vez sin canales */}
      {isFirstTime && (
        <div
          role="status"
          className="relative overflow-hidden rounded-xl border border-brand-200/60 bg-gradient-to-br from-brand-50 to-brand-100/40 px-4 py-4 flex items-start gap-3"
        >
          <div className="shrink-0 inline-flex w-10 h-10 items-center justify-center rounded-full hd-brand-solid bg-brand-500 text-white">
            <Sparkles className="w-5 h-5" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-fg">
              {t("Aún no tienes canales conectados")}
            </p>
            <p className="mt-1 text-xs text-fg-muted leading-relaxed">
              {t("Conecta al menos un canal para que tus clientes puedan escribirte desde WhatsApp, Messenger o Instagram. Sus mensajes llegarán directamente a la mesa de ayuda.")}
            </p>
          </div>
          <ArrowDown className="hidden sm:block w-4 h-4 text-brand-600 mt-1 animate-pulse" aria-hidden="true" />
        </div>
      )}

      {/* Grid de canales */}
      <section>
        <h2 className="text-[12px] md:text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-2">
          {t("Canales oficiales")}
        </h2>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {CHANNELS.map((ch) => (
            <ChannelCard
              key={ch.key}
              channel={ch}
              linked={getLinked(ch.key)}
              isConnecting={connecting === ch.key}
              onConnect={handleConnect}
              onTest={handleTest}
              testingId={testingId}
              onDisconnect={setDisconnectTarget}
              t={t}
            />
          ))}
        </div>
      </section>

      {/* Modal de desconexión */}
      <DisconnectModal
        isOpen={!!disconnectTarget}
        toggle={() => setDisconnectTarget(null)}
        integration={disconnectTarget}
        onConfirm={handleDisconnect}
      />

      {/* WhatsApp No Oficial */}
      <section className="pt-2">
        <UnofficialWhatsAppManager
          integrations={integrations}
          onRefreshIntegrations={fetchIntegrations}
        />
      </section>
    </div>
  );
}
